"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { 
  ExclamationTriangleIcon, 
  ClockIcon, 
  ChatBubbleLeftRightIcon, 
  UserGroupIcon,
  ChartBarIcon,
  FireIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

export default function Problem() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInFromLeft = {
    hidden: { opacity: 0, x: isMobile ? -20 : -50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInFromRight = {
    hidden: { opacity: 0, x: isMobile ? 20 : 50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.15
      }
    }
  };

  const problems = [
    {
      icon: ClockIcon,
      title: "Intégration longue et fastidieuse",
      description: "Les nouveaux collaborateurs attendent des réponses et l'équipe RH perd un temps précieux sur des tâches répétitives.",
      gradient: "from-red-500 to-pink-500",
      bgGradient: "from-red-50 to-pink-50",
      borderColor: "border-red-200",
      hoverBorder: "hover:border-red-400",
      animation: slideInFromLeft
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "Questions répétitives sans fin",
      description: "Les mêmes questions reviennent constamment : congés, processus, règlement... Une surcharge pour les équipes RH.",
      gradient: "from-orange-500 to-red-500",
      bgGradient: "from-orange-50 to-red-50",
      borderColor: "border-orange-200",
      hoverBorder: "hover:border-orange-400",
      animation: slideInFromRight
    },
    {
      icon: UserGroupIcon,
      title: "Équipes RH surchargées",
      description: "Moins de temps pour les missions stratégiques à cause des demandes basiques et répétitives.",
      gradient: "from-rose-500 to-red-500",
      bgGradient: "from-rose-50 to-red-50",
      borderColor: "border-rose-200",
      hoverBorder: "hover:border-rose-400",
      animation: slideInFromLeft
    },
    {
      icon: ChartBarIcon,
      title: "Onboarding inégal et chronophage",
      description: "La qualité de l'accueil varie selon la disponibilité des équipes, créant des expériences inconsistantes.",
      gradient: "from-amber-500 to-orange-500",
      bgGradient: "from-amber-50 to-orange-50",
      borderColor: "border-amber-200",
      hoverBorder: "hover:border-amber-400",
      animation: slideInFromRight
    }
  ];

  const impactStats = [
    {
      icon: FireIcon,
      number: "40%",
      label: "Temps perdu sur questions répétitives",
      gradient: "from-red-500 to-orange-500"
    },
    {
      icon: XCircleIcon,
      number: "73%",
      label: "Nouveaux collaborateurs insatisfaits",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: ExclamationTriangleIcon,
      number: "2x",
      label: "Plus de turnover en cas de mauvais onboarding",
      gradient: "from-rose-500 to-pink-500"
    }
  ];

  return (
    <section id="problem" className={`py-16 bg-gradient-to-br from-red-50/30 via-orange-50/20 to-gray-50/30 relative overflow-hidden smooth-scroll-section ${isMobile ? 'mobile-simple' : ''}`}>
      {/* Éléments décoratifs animés - désactivés sur mobile */}
      {!isMobile && (
        <>
          {/* Cercles décoratifs supprimés */}
        </>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1, margin: isMobile ? "-50px" : "-100px" }}
          className="text-center"
        >
          {/* Badge section */}
          <motion.div
            variants={scaleIn}
            whileHover={isMobile ? {} : { scale: 1.05 }}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-full px-6 py-3 mb-8 shadow-lg backdrop-blur-sm"
          >
            {!isMobile ? (
              <motion.div 
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-6 h-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center"
              >
                <ExclamationTriangleIcon className="w-4 h-4 text-white" />
              </motion.div>
            ) : (
              <div className="w-6 h-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="w-4 h-4 text-white" />
              </div>
            )}
            <span className="text-sm font-semibold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
              Les défis actuels
            </span>
          </motion.div>

          {/* Titre principal */}
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-relaxed py-4"
            style={{ lineHeight: '1.3' }}
          >
            <motion.span
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "linear" 
              }}
              className="inline-block bg-gradient-to-r from-gray-900 via-red-600 to-gray-900 bg-[length:200%_100%] bg-clip-text text-transparent"
            >
              Les entreprises font face
            </motion.span>
            <span className="block bg-gradient-to-r from-red-600 to-orange-600 gradient-text-safe mt-2">
              à des défis RH majeurs
            </span>
          </motion.h2>

          {/* Description */}
          <motion.div
            variants={fadeInUp}
            className="max-w-3xl mx-auto mb-16"
          >
            <p className="text-xl text-gray-600 leading-relaxed">
              L'onboarding et la gestion des questions RH représentent aujourd'hui{" "}
              <span className="font-semibold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                un défi de taille
              </span>{" "}
              pour les entreprises, entraînant une perte de temps et d'efficacité considérable.
            </p>
          </motion.div>

          {/* Statistiques d'impact */}
          <motion.div
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-20"
          >
            {impactStats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ 
                  scale: 1.05,
                  transition: { duration: 0.2 }
                }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-red-200 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
              >
                {/* Effet de brillance au survol */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-100/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <div className={`w-12 h-12 bg-gradient-to-r ${stat.gradient} rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg relative`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className={`text-3xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent mb-2`}
                >
                  {stat.number}
                </motion.div>
                <p className="text-sm text-gray-600 font-medium leading-tight">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Grille des problèmes */}
          <motion.div
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16"
          >
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                variants={problem.animation}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className={`
                  bg-gradient-to-br ${problem.bgGradient} rounded-3xl p-8 border-2 ${problem.borderColor} ${problem.hoverBorder}
                  transition-all duration-300 hover:shadow-2xl relative overflow-hidden group
                `}
              >
                {/* Effet de brillance au survol */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <div className="flex items-start space-x-4 relative">
                  <div className="flex-shrink-0">
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      className={`w-16 h-16 bg-gradient-to-r ${problem.gradient} rounded-2xl flex items-center justify-center shadow-lg`}
                    >
                      <problem.icon className="w-8 h-8 text-white" />
                    </motion.div>
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {problem.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {problem.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Citation améliorée */}
          <motion.div
            variants={fadeInUp}
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-br from-white/90 to-red-50/50 backdrop-blur-sm rounded-3xl p-8 border-2 border-red-200/60 max-w-4xl mx-auto shadow-lg relative overflow-hidden group"
          >
            {/* Effet de brillance au survol */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-100/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl flex items-center justify-center mb-6 mx-auto">
                <ExclamationTriangleIcon className="w-6 h-6 text-white" />
              </div>
              <blockquote className="text-lg text-gray-700 font-medium mb-4 italic">
                "
                <span className="font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                  55 % des professionnels RH
                </span>{" "}
                déclarent consacrer{" "}
                <span className="font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                  au moins la moitié de leur temps
                </span>{" "}
                à des tâches administratives répétitives, ce qui limite leur capacité à se concentrer sur des enjeux plus stratégiques."
              </blockquote>
              <cite className="text-sm text-gray-500 font-medium">
                — Baromètre RH 2024, Parlons RH
              </cite>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
} 